import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import find from 'lodash/find';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {
  handleAccountLimitErrors,
  providesTagsByURL,
  showLoadingBar,
  showMessages,
  UPGRADE_MODALS,
} from 'app/store/sweet-api/utils';
import {
  TEAM_FOLDERS_API_URL,
  TEAM_POLICIES_API_URL,
  TEAM_PROCEDURES_API_URL,
  TEAM_PROCESSES_API_URL,
  TEAM_QUIZZES_API_URL,
  TEAMS_API_URL,
} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt, ngt} from 'app/utils/i18n';
import {
  DOCUMENT_TAG,
  FOLDER_TAGS,
  POLICY_RTK_TAGS,
  PROCEDURE_RTK_TAGS,
  PROCESS_RTK_TAGS,
  QUIZ_TAGS,
  SEARCH_TAG,
  SIGNOFF_TAGS,
  TEAMTAG,
  USER_RTK_TAG,
} from 'app/utils/rtk-tags';

import type {
  BulkAddRemoveDocumentsParams,
  TeamsFetchParamsType,
  TeamsLocationParamsType,
} from './types';
import type {
  CompletedUserJobSerializedType,
  ListResponseType,
  TeamSerializedType,
  UserJobSerializedType,
} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [
      TEAMTAG,
      DOCUMENT_TAG,
      POLICY_RTK_TAGS.TAGS,
      POLICY_RTK_TAGS.HASHID,
      PROCEDURE_RTK_TAGS.TAGS,
      PROCEDURE_RTK_TAGS.HASHID,
      PROCESS_RTK_TAGS.TAGS,
      PROCESS_RTK_TAGS.HASHID,
      USER_RTK_TAG,
      FOLDER_TAGS.TAGS,
      SEARCH_TAG,
      SIGNOFF_TAGS.TAGS,
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getTeamDetail: build.query<TeamSerializedType, number | string>({
        query: (id: number | string) => `${TEAMS_API_URL}${id}/`,
        providesTags: () => [{type: TEAMTAG, id: 'list'}],
        onQueryStarted: showLoadingBar,
      }),

      getTeams: build.query<
        ListResponseType<TeamSerializedType>,
        TeamsFetchParamsType
      >({
        query: (params) => ({
          url: TEAMS_API_URL,
          params,
        }),
        onQueryStarted: showLoadingBar,
        providesTags: providesTagsByURL(TEAMTAG),
      }),

      createTeam: build.mutation<
        TeamSerializedType,
        {
          name: string;
          description?: string;
        }
      >({
        query: ({name, description}) => ({
          url: TEAMS_API_URL,
          method: 'POST',
          body: {name, description},
        }),
        onQueryStarted: (...args) => {
          showMessages({
            onSuccess: ({name}) => gt('Successfully created team %s', name),
            onError: () => gt('Unable to create team'),
          })(...args);

          handleAccountLimitErrors(UPGRADE_MODALS.TEAM_LIMIT)(...args);
        },
        invalidatesTags: () => [
          {type: TEAMTAG, id: 'list'},
          {type: SEARCH_TAG, id: 'list'},
        ],
      }),

      createTeams: build.mutation<
        Array<TeamSerializedType>,
        Array<{name: string; description?: string}>
      >({
        query: (teams) => ({
          url: TEAMS_API_URL,
          method: 'POST',
          body: teams,
        }),
        onQueryStarted: (teams, ...args) => {
          showMessages({
            onSuccess: (teams: Array<{name: string; description?: string}>) =>
              ngt(
                'Successfully created %d team',
                'Successfully created %d teams',
                teams.length
              ),
            onError: () => gt('Unable to create teams'),
          })(teams, ...args);
          handleAccountLimitErrors(UPGRADE_MODALS.TEAM_LIMIT)(teams, ...args);
        },
        invalidatesTags: () => [{type: TEAMTAG, id: 'list'}],
      }),

      updateTeam: build.mutation<
        TeamSerializedType,
        {
          teamid: string | number;
          body: {
            name?: string;
            description?: string;
            teammates_can_assign_task?: boolean;
          };
        }
      >({
        query: ({teamid, body}) => ({
          url: `${TEAMS_API_URL}${teamid}/`,
          method: 'PATCH',
          body,
        }),
        onQueryStarted: showMessages({
          onError: () => gt('Unable to update team'),
        }),
        invalidatesTags: (response) => [
          {type: TEAMTAG, id: 'list'},
          ...(response != null
            ? [
                {type: TEAMTAG, id: response.url} as {
                  type: typeof TEAMTAG;
                  id: string;
                },
              ]
            : []),
        ],
      }),

      deleteTeam: build.mutation<unknown, {id: string | number; name?: string}>(
        {
          query: ({id}) => ({
            url: `${TEAMS_API_URL}${id}/`,
            method: 'DELETE',
          }),
          onQueryStarted: showMessages({
            onSuccess: ({name}) => gt('Deleted team %s', name || ''),
            onError: () => gt('Unable to delete team at the moment'),
          }),
          invalidatesTags: () => [{type: TEAMTAG, id: 'list'}],
        }
      ),

      updateMemberAccess: build.mutation<
        unknown,
        {
          url: string;
          access: 'manager' | 'teammate' | 'reader';
          userUrl: string;
        }
      >({
        query: ({url, access}) => ({
          url,
          method: 'PATCH',
          body: {access},
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Member access updated'),
          onError: () => gt('Unable to update member access at the moment'),
        }),
        invalidatesTags: ({userUrl}) => [
          {type: TEAMTAG, id: 'list'},
          {type: USER_RTK_TAG, id: userUrl},
        ],
      }),

      addProceduresToTeam: build.mutation<
        unknown,
        {team: TeamSerializedType; urls: Array<string>}
      >({
        queryFn: ({team, urls}, api, extraOptions, baseQuery) => {
          return baseQuery({
            url: TEAM_PROCEDURES_API_URL,
            method: 'POST',
            body: urls.map((url) => ({
              procedure: url,
              team: team.url,
            })),
          });
        },
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: ({urls}) =>
              ngt('Added procedure', 'Added procedures', urls.length),
            onError: () =>
              ngt(
                'Unable to add the procedure to the team at the moment',
                'Unable to add those procedures to the team at the moment',
                args.urls.length
              ),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: TEAMTAG, id: 'list'},
          {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
          {type: PROCEDURE_RTK_TAGS.HASHID, id: 'list'},
          {type: SIGNOFF_TAGS.TAGS, id: 'list'},
        ],
      }),

      detachFromToTeam: build.mutation<Record<string, never>, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => {
            return gt('Removed from team');
          },
          onError: () => {
            return gt('Could not remove from team.');
          },
        }),
      }),

      addProcessesToTeam: build.mutation<
        unknown,
        {team: TeamSerializedType; urls: Array<string>}
      >({
        queryFn: ({team, urls}, api, extraOptions, baseQuery) => {
          return baseQuery({
            url: TEAM_PROCESSES_API_URL,
            method: 'POST',
            body: urls.map((url) => ({
              process: url,
              team: team.url,
            })),
          });
        },
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: ({urls}) =>
              ngt('Added process', 'Added processes', urls.length),
            onError: () =>
              ngt(
                'Unable to add the process to the team at the moment',
                'Unable to add those processes to the team at the moment',
                args.urls.length
              ),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: TEAMTAG, id: 'list'},
          {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
          {type: PROCESS_RTK_TAGS.HASHID, id: 'list'},
          {type: SIGNOFF_TAGS.TAGS, id: 'list'},
        ],
      }),

      addPoliciesToTeam: build.mutation<
        unknown,
        {team: TeamSerializedType; urls: Array<string>}
      >({
        queryFn: ({team, urls}, api, extraOptions, baseQuery) => {
          return baseQuery({
            url: TEAM_POLICIES_API_URL,
            method: 'POST',
            body: urls.map((url) => ({
              policy: url,
              team: team.url,
            })),
          });
        },
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: ({urls}) =>
              ngt('Added policy', 'Added policies', urls.length),
            onError: () =>
              ngt(
                'Unable to add the policy to the team at the moment',
                'Unable to add those policies to the team at the moment',
                args.urls.length
              ),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: TEAMTAG, id: 'list'},
          {type: POLICY_RTK_TAGS.TAGS, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
          {type: POLICY_RTK_TAGS.HASHID, id: 'list'},
          {type: SIGNOFF_TAGS.TAGS, id: 'list'},
        ],
      }),
      addQuizzesToTeam: build.mutation<
        unknown,
        {team: TeamSerializedType; urls: Array<string>}
      >({
        queryFn: ({team, urls}, api, extraOptions, baseQuery) => {
          return baseQuery({
            url: TEAM_QUIZZES_API_URL,
            method: 'POST',
            body: urls.map((url) => ({
              quiz: url,
              team: team.url,
            })),
          });
        },
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: ({urls}) =>
              ngt('Added quiz', 'Added quizzes', urls.length),
            onError: () =>
              ngt(
                'Unable to add the quiz to the team at the moment',
                'Unable to add those quizzes to the team at the moment',
                args.urls.length
              ),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: TEAMTAG, id: 'list'},
          {type: QUIZ_TAGS.TAG, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
        ],
      }),

      addFoldersToTeam: build.mutation<
        unknown,
        {team: TeamSerializedType; urls: Array<string>}
      >({
        queryFn: ({team, urls}, api, extraOptions, baseQuery) => {
          return baseQuery({
            url: TEAM_FOLDERS_API_URL,
            method: 'POST',
            body: urls.map((url) => ({
              folder: url,
              team: team.url,
            })),
          });
        },
        invalidatesTags: [
          {type: FOLDER_TAGS.TAGS, id: 'list'},
          {type: TEAMTAG, id: 'list'},
        ],
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: ({urls}) =>
              ngt('Added folder', 'Added folders', urls.length),
            onError: () =>
              ngt(
                'Unable to add the folder to the team at the moment',
                'Unable to add those folders to the team at the moment',
                args.urls.length
              ),
          })(args, api);
        },
      }),

      removeTeamItem: build.mutation<
        unknown,
        {
          teamURL: string;
          item: {
            team_memberships: Array<{team: string; url: string}>;
            url: string;
          };
        }
      >({
        queryFn: ({teamURL, item}, api, extraOptions, baseQuery) => {
          const membership = find(item.team_memberships, {team: teamURL});

          if (membership != null) {
            return baseQuery({
              url: membership.url,
              method: 'DELETE',
            }) as any;
          } else {
            return {error: {data: {message: gt('Not in the team')}}};
          }
        },

        onQueryStarted: showMessages({
          onSuccess: () => gt('Removed from team'),
          onError: {
            fallback: (response) =>
              response?.[0] ?? gt('Could not remove from team'),
            byStatus: {
              404: () => gt('Not in the team'),
            },
          },
        }),
        invalidatesTags: (response, error, {item}) => [
          {type: TEAMTAG, id: 'list'},
          {type: POLICY_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCEDURE_RTK_TAGS.HASHID, id: item.url},
          {type: DOCUMENT_TAG, id: 'list'},
          {type: USER_RTK_TAG, id: 'list'},
          {type: USER_RTK_TAG, id: item.url},
          {type: QUIZ_TAGS.TAG, id: 'list'},
          {type: FOLDER_TAGS.TAGS, id: 'list'},
        ],
      }),
      bulkRemoveDocumentsFromTeam: build.mutation<
        CompletedUserJobSerializedType,
        BulkAddRemoveDocumentsParams
      >({
        queryFn: (
          {documents, team},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${TEAMS_API_URL}bulk_remove_documents/`,
                    method: 'POST',
                    body: {
                      documents,
                      team,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [
          {type: TEAMTAG, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
        ],
        onQueryStarted: ({documents, ...args}, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              ngt(
                'The selected document is being removed from the team...',
                'The selected documents are being removed from the team...',
                documents.length
              ),
            onSuccess: () => [
              ngt(
                'The selected document was removed from the team.',
                'The selected documents were removed from the team.',
                documents.length
              ),
              {level: 'success'},
            ],
            onError: () =>
              ngt(
                "There was an error and we couldn't remove the selected document from the team.",
                "There was an error and we couldn't remove the selected documents from the team.",
                documents.length
              ),
          })(args, api);
        },
      }),
      bulkAddDocumentsToTeam: build.mutation<
        CompletedUserJobSerializedType,
        BulkAddRemoveDocumentsParams
      >({
        queryFn: (
          {documents, team},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${TEAMS_API_URL}bulk_add_documents/`,
                    method: 'POST',
                    body: {
                      documents,
                      team,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [
          {type: TEAMTAG, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
          {type: POLICY_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
        ],
        onQueryStarted: ({documents, ...args}, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              ngt(
                'The document is being added to the team...',
                'The documents are being added to the team...',
                documents.length
              ),
            onSuccess: () => [
              ngt(
                'The document was added to the team.',
                'The documents were added to the team.',
                documents.length
              ),
              {level: 'success'},
            ],
            onError: () =>
              ngt(
                "There was an error and we couldn't add the selected document to the team.",
                "There was an error and we couldn't add the selected documents to the team.",
                documents.length
              ),
          })(args, api);
        },
      }),
    }),
  });

export const {
  useGetTeamsQuery: useTeams,
  useGetTeamDetailQuery: useTeamDetail,
  useCreateTeamMutation: useCreateTeam,
  useCreateTeamsMutation: useCreateTeams,
  useUpdateTeamMutation: useUpdateTeam,
  useDeleteTeamMutation: useDeleteTeam,
  useRemoveTeamItemMutation: useRemoveTeamItem,
  useAddProceduresToTeamMutation: useAddProceduresToTeam,
  useAddPoliciesToTeamMutation: useAddPoliciesToTeam,
  useAddProcessesToTeamMutation: useAddProcessesToTeam,
  useAddFoldersToTeamMutation: useAddFoldersToTeam,
  useAddQuizzesToTeamMutation: useAddQuizzesToTeam,
  useBulkAddDocumentsToTeamMutation: useBulkAddDocumentsToTeam,
  useBulkRemoveDocumentsFromTeamMutation: useBulkRemoveDocumentsFromTeam,
  useDetachFromToTeamMutation: useDetachFromTeam,
  useUpdateMemberAccessMutation: useUpdateMemberAccess,
} = extendedAPI;
export const convertLocationParamsToFetchParams = (
  query: TeamsLocationParamsType
): TeamsFetchParamsType => {
  return {
    ...query,
    page: query.page != null ? query.page : '1',
    page_size:
      query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE.toString(),
  };
};
